import { cloneElement, useId, type ReactElement } from "react";

import * as css from "~/components/Field.css";

/** The props Field reads off its control and the ones it wires into it. */
export interface FieldControlProps {
  id?: string;
  required?: boolean | undefined;
  "aria-labelledby"?: string | undefined;
  "aria-describedby"?: string | undefined;
  "aria-invalid"?: boolean | undefined;
}

export interface FieldProps {
  label: string;
  /** A refusal of this control's value. Takes the line over the hint. */
  message?: string | undefined;
  /** Muted text in the message line while there is no message. */
  hint?: string | undefined;
  /** A quiet word at the label's right, such as "Saved". */
  status?: string | undefined;
  className?: string | undefined;
  /** The control is a group of inputs, such as radios: the label becomes the
   *  legend of a fieldset around it. */
  group?: boolean | undefined;
  children: ReactElement<FieldControlProps>;
}

/**
 * A form control with its label and its message line. The required mark is
 * read off the control's own `required`, so the attribute and the mark cannot
 * disagree.
 */
export function Field({ label, message, hint, status, className, group, children }: FieldProps) {
  const id = useId();
  const lineId = `${id}-line`;
  const legendId = `${id}-legend`;
  const invalid = message !== undefined && message !== "";
  const line = invalid ? message : (hint ?? "");
  const merged = className !== undefined && className.length > 0 ? `${css.field} ${className}` : css.field;
  const heading = (
    <>
      {label}
      {children.props.required === true ? (
        <span className={css.mark} aria-hidden="true">
          *
        </span>
      ) : null}
    </>
  );
  const caption = status !== undefined ? <span className={css.caption}>{status}</span> : null;
  const control = cloneElement(children, {
    id,
    "aria-labelledby": group === true ? legendId : undefined,
    "aria-describedby": line !== "" ? lineId : undefined,
    "aria-invalid": invalid ? true : undefined,
  });
  const messageLine = (
    <span id={lineId} className={invalid ? css.message : css.hint}>
      {line}
    </span>
  );

  if (group === true) {
    return (
      <fieldset className={`${merged} ${css.group}`}>
        <legend id={legendId} className={`${css.labelRow} ${css.legend}`}>
          <span>{heading}</span>
        </legend>
        {status !== undefined ? <span className={`${css.caption} ${css.legendCaption}`}>{status}</span> : null}
        {control}
        {messageLine}
      </fieldset>
    );
  }

  return (
    <div className={merged}>
      <div className={css.labelRow}>
        <label htmlFor={id}>{heading}</label>
        {caption}
      </div>
      {control}
      {messageLine}
    </div>
  );
}
